import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import ChecklistPanel from "./ChecklistPanel";
import DevolucionesPanel from "./DevolucionesPanel";

const ESTADO_LABELS = {
  ACTIVO: "Activo",
  BAJA: "Baja",
  FUTURO: "Futuro",
  VENTA: "Venta",
};

const ESTADO_COLORS = {
  ACTIVO: { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  BAJA: { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
  FUTURO: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
  VENTA: { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" },
};

const normalizeEstado = (estado) => String(estado || "ACTIVO").toUpperCase();

const formatEstado = (estado) => {
  const key = normalizeEstado(estado);
  return ESTADO_LABELS[key] || key;
};

const getEstadoColors = (estado) =>
  ESTADO_COLORS[normalizeEstado(estado)] || ESTADO_COLORS.ACTIVO;

function PanelShell({ title, right, children, sx }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        borderColor: "#e7ebf3",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
        ...sx,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          borderBottom: "1px solid #eef2f7",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#071942" }}>
          {title}
        </Typography>
        {right}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Paper>
  );
}

export default function SeguimientoDialog({
  open,
  onClose,
  titulo = "Seguimiento",
  checklistProps,
  devolucionesProps,
  adminReadOnly = false,
  vehiculo = "",
  estado = "ACTIVO",
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [tab, setTab] = React.useState(0);

  const panelHeight = 474;
  const estadoColors = getEstadoColors(estado);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          height: "90vh",
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "flex-start",
          p: 2,
        }}
      >
        <Box sx={{ minWidth: 0, pr: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#071942", lineHeight: 1 }}>
            {titulo}
          </Typography>
          {(vehiculo || estado) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
              {vehiculo && (
                <Typography sx={{ fontSize: 12, color: "#51617f", fontWeight: 700 }}>
                  {vehiculo}
                </Typography>
              )}
              {estado && (
                <Chip
                  size="small"
                  label={formatEstado(estado)}
                  sx={{
                    height: 20,
                    borderRadius: 1,
                    bgcolor: estadoColors.bg,
                    color: estadoColors.color,
                    border: `1px solid ${estadoColors.border}`,
                    fontSize: 11,
                    fontWeight: 800,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              )}
            </Box>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {isMdUp ? (
          <Box
            sx={{
              px: 2,
              display: "grid",
              gridTemplateColumns: "64% 1fr",
              gap: 2,
              height: panelHeight,
              minHeight: 0,
            }}
          >
            <PanelShell title="Historial">
              <DevolucionesPanel {...devolucionesProps} height="100%" />
            </PanelShell>

            <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, gap: 2 }}>
              <PanelShell title="Checklist comercial" sx={{ flex: 1, height: "auto" }}>
                <ChecklistPanel {...checklistProps} height="100%" />
              </PanelShell>

              {adminReadOnly && (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    borderColor: "#dbeafe",
                    bgcolor: "#eff6ff",
                    p: 2,
                    display: "grid",
                    gridTemplateColumns: "34px 1fr",
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      color: "#2563eb",
                      bgcolor: "#dbeafe",
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0f1f4d" }}>
                      Modo solo lectura
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#41557f", mt: 0.25 }}>
                      No tenés permisos para agregar devoluciones desde esta vista.
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="fullWidth"
            >
              <Tab label="Devoluciones" />
              <Tab label="Checklist" />
            </Tabs>

            <Box sx={{ mt: 2, height: panelHeight }}>
              {tab === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 1.5, overflow: "hidden", height: "100%" }}
                >
                  <DevolucionesPanel {...devolucionesProps} height="100%" />
                </Paper>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 2 }}>
                  <Paper
                    variant="outlined"
                    sx={{ borderRadius: 1.5, overflow: "hidden", flex: 1, minHeight: 0 }}
                  >
                    <ChecklistPanel {...checklistProps} height="100%" />
                  </Paper>
                  {adminReadOnly && (
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 1.5,
                        borderColor: "#dbeafe",
                        bgcolor: "#eff6ff",
                        p: 2,
                        display: "grid",
                        gridTemplateColumns: "34px 1fr",
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          color: "#2563eb",
                          bgcolor: "#dbeafe",
                        }}
                      >
                        <LockOutlinedIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0f1f4d" }}>
                          Modo solo lectura
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#41557f", mt: 0.25 }}>
                          No tenés permisos para agregar devoluciones desde esta vista.
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
